import React, { useState } from 'react';
import { MaterialPickerCombobox } from './MaterialPickerCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    Trash2,
    Plus,
    SplitSquareHorizontal,
    LayoutTemplate,
    Box,
    Package,
    Sparkles,
    ArrowLeftRight,
    RotateCcw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useStore } from '@tanstack/react-form';
import {
    calculateFilmMetrics,
    parseSheetDimensionsMm,
    type LaminationFilmType,
} from '@/pages/planning/production-orders/utils/po-auto-planner.engine';

export function MaterialPlanList({
    form,
    isReadOnly = false,
    onRegenerateAutoPlans,
}: {
    form: any;
    isReadOnly?: boolean;
    onRegenerateAutoPlans?: (forceResetCustom?: boolean) => void;
}) {
    const printLayouts = useStore(form.store, (state: any) => state.values.printLayouts || []);
    const materialPlans = useStore(form.store, (state: any) => state.values.materialPlans || []);
    const printingPaperSize = useStore(form.store, (state: any) => state.values.printingPaperSize || '');
    const purchasedPaperSize = useStore(form.store, (state: any) => state.values.purchasedPaperSize || '');
    const status = useStore(form.store, (state: any) => state.values.status || 'DRAFT');

    // Zero Hard-Lock before RELEASED
    const isLocked = isReadOnly || ['RELEASED', 'IN_PROGRESS', 'MANUFACTURED', 'COMPLETED', 'CLOSED'].includes(status);

    const setMaterialPlans = (updater: any) => {
        const current = form.getFieldValue('materialPlans') || [];
        const newValue = typeof updater === 'function' ? updater(current) : updater;
        form.setFieldValue('materialPlans', newValue);
    };

    const syncToBomItems = (updatedPlans: any[]) => {
        const currentComponents = form.getFieldValue('components') || [];
        if (currentComponents.length === 0) return;
        const nextComponents = [...currentComponents];
        const comp0 = { ...nextComponents[0] };
        comp0.bomItems = updatedPlans.map((plan: any) => {
            const req = Number(plan.sheetsRequired || 0);
            const waste = Number(plan.wastageSheets || 0);
            const total = Number(plan.totalSheets ?? req + waste);
            const wastePct = req > 0 ? Number(((waste / req) * 100).toFixed(1)) : 0;
            const specParts = [
                plan._gsm ? `${plan._gsm}` : '',
                plan._size ? `Khổ ${plan._size}mm` : '',
                plan.linearMeters ? `~${plan.linearMeters}m (${plan.weightKg || 0}kg)` : '',
            ].filter(Boolean);
            return {
                materialId: plan.plannedMaterialId || plan.materialId || plan.materialCode || '',
                materialCode: plan.materialCode || '',
                materialName: plan.materialName || '',
                materialCategory: plan.materialCategory || 'PAPER',
                specification: plan.specification || specParts.join(' • '),
                unit: plan.unit || 'Tờ',
                quantity: req,
                qtyRequired: req,
                wastagePercent: wastePct,
                totalQuantity: total,
                _gsm: plan._gsm || '',
                _size: plan._size || '',
                linearMeters: plan.linearMeters,
                weightKg: plan.weightKg,
                notes: plan.notes || plan.splitReason || '',
                isAutoGenerated: Boolean(plan.isAutoGenerated),
                isUserCustomized: Boolean(plan.isUserCustomized),
                autoRuleKey: plan.autoRuleKey,
            };
        });
        nextComponents[0] = comp0;
        form.setFieldValue('components', nextComponents);
    };

    const recalculateRowMetrics = (row: any) => {
        const sheetsRequired = Math.max(0, Number(row.sheetsRequired || 0));
        const wastageSheets = Math.max(0, Number(row.wastageSheets || 0));
        const totalSheets =
            row.totalSheets !== undefined && row._keepDirectTotal
                ? Math.max(0, Number(row.totalSheets || 0))
                : sheetsRequired + wastageSheets;

        const isFilm =
            row.materialCategory === 'FILM' ||
            String(row.materialCode || '').toUpperCase().includes('MANG') ||
            String(row.materialName || '').toLowerCase().includes('màng');

        let linearMeters = row.linearMeters;
        let weightKg = row.weightKg;
        let rollWidthMm = row.rollWidthMm;
        let feedLengthMm = row.feedLengthMm;

        if (isFilm) {
            const parsedSize = parseSheetDimensionsMm(row._size || `${row.rollWidthMm || 0}x${row.feedLengthMm || 0}`);
            if (parsedSize) {
                rollWidthMm = parsedSize.rawWidthMm;
                feedLengthMm = parsedSize.rawLengthMm;
            }
            if (rollWidthMm > 0 && feedLengthMm > 0) {
                const codeUpper = String(row.materialCode || '').toUpperCase();
                const filmType: LaminationFilmType = codeUpper.includes('MET')
                    ? 'MANG_METALIZE'
                    : codeUpper.includes('NHUNG')
                      ? 'MANG_NHUNG'
                      : codeUpper.includes('BONG')
                        ? 'MANG_BONG'
                        : 'MANG_MO';
                const metrics = calculateFilmMetrics({
                    rollWidthMm,
                    feedLengthMm,
                    totalSheets,
                    filmType,
                });
                linearMeters = metrics.linearMeters;
                weightKg = metrics.weightKg;
            }
        }

        const { _keepDirectTotal, ...cleanRow } = row;
        return {
            ...cleanRow,
            sheetsRequired,
            wastageSheets,
            totalSheets,
            rollWidthMm,
            feedLengthMm,
            linearMeters,
            weightKg,
        };
    };

    const addMaterialPlan = (plan: any) => {
        setMaterialPlans((prev: any[]) => {
            const next = [...prev, recalculateRowMetrics(plan)];
            syncToBomItems(next);
            return next;
        });
    };

    const updateMaterialPlan = (rowKey: string, updates: any, markCustomized = true) => {
        setMaterialPlans((prev: any[]) => {
            const next = prev.map((p: any, idx: number) => {
                const currentKey = p.id || p.autoRuleKey || `idx-${idx}`;
                if (currentKey !== rowKey) return p;
                const merged = {
                    ...p,
                    ...updates,
                    isUserCustomized: markCustomized ? true : p.isUserCustomized,
                };
                return recalculateRowMetrics(merged);
            });
            syncToBomItems(next);
            return next;
        });
    };

    const removeMaterialPlan = (rowKey: string) => {
        setMaterialPlans((prev: any[]) => {
            const next = prev.filter((p: any, idx: number) => {
                const currentKey = p.id || p.autoRuleKey || `idx-${idx}`;
                return currentKey !== rowKey;
            });
            syncToBomItems(next);
            return next;
        });
    };

    const [viewMode, setViewMode] = useState<'component' | 'layout'>('component');

    const handleAdd = () => {
        if (isLocked) return;
        const dims = parseSheetDimensionsMm(printingPaperSize || purchasedPaperSize);
        addMaterialPlan({
            id: crypto.randomUUID(),
            materialId: '',
            plannedMaterialId: '',
            originalMaterialId: '',
            materialCode: '',
            materialName: '',
            materialCategory: 'PAPER',
            supplierId: '',
            sheetsRequired: Number(form.getFieldValue('standardQuantity') || 0),
            wastageSheets: Number(form.getFieldValue('allowance') || 0),
            totalSheets:
                Number(form.getFieldValue('standardQuantity') || 0) +
                Number(form.getFieldValue('allowance') || 0),
            unit: 'Tờ',
            notes: '',
            componentId: 'COMP_1',
            printLayoutId: printLayouts[0]?.id || '',
            _gsm: '',
            _size: dims ? dims.formattedMm : '',
            isUserCustomized: true,
        });
    };

    const handleSplit = (plan: any, rowKey: string) => {
        if (isLocked) return;
        const halfReq = Math.floor(Number(plan.sheetsRequired || 0) / 2);
        const remainReq = Math.ceil(Number(plan.sheetsRequired || 0) / 2);

        updateMaterialPlan(
            rowKey,
            {
                sheetsRequired: remainReq,
                isCustomQuantity: true,
            },
            true
        );

        addMaterialPlan({
            ...plan,
            id: crypto.randomUUID(),
            autoRuleKey: undefined,
            sheetsRequired: halfReq,
            wastageSheets: 0,
            totalSheets: halfReq,
            isCustomQuantity: true,
            isUserCustomized: true,
            notes: `${plan.notes || plan.splitReason || ''} (Tách dòng)`.trim(),
        });
    };

    const handleToggleFilmOrientation = (plan: any, rowKey: string) => {
        if (isLocked) return;
        const sheetDims = parseSheetDimensionsMm(printingPaperSize || purchasedPaperSize || plan._size);
        const longEdge = sheetDims ? sheetDims.longEdgeMm : 650;
        const shortEdge = sheetDims ? sheetDims.shortEdgeMm : 430;

        const currentDir = plan.feedDirection || 'HORIZONTAL';
        const nextDir = currentDir === 'HORIZONTAL' ? 'VERTICAL' : 'HORIZONTAL';
        const nextRollWidth = nextDir === 'HORIZONTAL' ? Math.max(10, longEdge - 5) : Math.max(10, shortEdge - 5);
        const nextFeedLength = nextDir === 'HORIZONTAL' ? shortEdge : longEdge;

        const oldCode = String(plan.materialCode || 'VT-MANG-MO-645');
        const nextCode = oldCode.replace(/-\d{3,4}$/, `-${nextRollWidth}`);
        const oldName = String(plan.materialName || `Màng BOPP Mờ khổ ${nextRollWidth}mm`);
        const nextName = oldName.replace(/\d{3,4}\s*mm/i, `${nextRollWidth}mm`);
        const dirLabel = nextDir === 'HORIZONTAL' ? 'Khổ ngang -5mm' : 'Khổ dọc -5mm';

        updateMaterialPlan(
            rowKey,
            {
                materialCode: nextCode,
                materialName: nextName,
                plannedMaterialId: plan.isAutoGenerated ? nextCode : plan.plannedMaterialId,
                originalMaterialId: plan.isAutoGenerated ? nextCode : plan.originalMaterialId,
                _size: `${nextRollWidth}x${nextFeedLength}`,
                rollWidthMm: nextRollWidth,
                feedLengthMm: nextFeedLength,
                feedDirection: nextDir,
                splitReason: `${dirLabel} (${nextRollWidth}mm, bước cắt ${nextFeedLength}mm)`,
            },
            true
        );
    };

    const handleMaterialSelect = (material: any, rowKey: string) => {
        if (!material) {
            updateMaterialPlan(rowKey, {
                materialId: '',
                plannedMaterialId: '',
                originalMaterialId: '',
                materialCode: '',
                materialName: '',
                _gsm: '',
                _size: '',
            });
            return;
        }

        const gsm = material.gsm ? String(material.gsm) : '';
        const size =
            material.width && material.length
                ? `${material.width}x${material.length}`
                : material.width
                  ? `${material.width}mm`
                  : '';

        updateMaterialPlan(rowKey, {
            materialId: material.id,
            plannedMaterialId: material.id,
            originalMaterialId: material.id,
            materialCode: material.materialId || material.code || '',
            materialName: material.name || '',
            materialCategory: material.category || 'PAPER',
            _gsm: gsm,
            _size: size || undefined,
            isAutoGenerated: Boolean(material.isAutoGenerated),
            availableStock: Number(material.currentStock || 0),
        });
    };

    // Grouping logic based on viewMode
    const groupedPlans = materialPlans.reduce((acc: Record<string, any[]>, plan: any, idx: number) => {
        const key =
            viewMode === 'component'
                ? plan.componentId || 'Thành phần chính (Bài in)'
                : printLayouts.find((l: any) => l.id === plan.printLayoutId)?.name || 'Bài in chính';
        if (!acc[key]) acc[key] = [];
        acc[key].push({ ...plan, _rowKey: plan.id || plan.autoRuleKey || `idx-${idx}` });
        return acc;
    }, {} as Record<string, any[]>);

    return (
        <div className="space-y-3" data-testid="material-plan-list">
            <div className="flex flex-wrap justify-between items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200/80">
                <div className="flex items-center gap-2">
                    <div className="flex bg-white rounded-md p-0.5 shadow-sm border border-slate-200">
                        <Button
                            type="button"
                            size="sm"
                            variant={viewMode === 'component' ? 'default' : 'ghost'}
                            className={cn('h-7 px-3 text-xs', viewMode === 'component' && 'shadow-sm')}
                            onClick={() => setViewMode('component')}
                        >
                            <Box className="w-3.5 h-3.5 mr-1.5" />
                            Theo Thành Phần
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={viewMode === 'layout' ? 'default' : 'ghost'}
                            className={cn('h-7 px-3 text-xs', viewMode === 'layout' && 'shadow-sm')}
                            onClick={() => setViewMode('layout')}
                        >
                            <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" />
                            Theo Bài In
                        </Button>
                    </div>

                    <span className="text-[11px] text-slate-500 hidden md:inline">
                        Tự động tính Giấy in, Màng cán (ưu tiên Khổ ngang -5mm / Khổ dọc -5mm) & Vật tư bồi theo khổ in. Cho phép tùy chỉnh 100% trước khi ban hành.
                    </span>
                </div>

                {!isLocked && (
                    <div className="flex items-center gap-2">
                        {onRegenerateAutoPlans && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onRegenerateAutoPlans(true)}
                                className="h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                data-testid="btn-regenerate-material-plans"
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-indigo-600" />
                                Tự động tính lại theo Khổ in
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={handleAdd}
                            size="sm"
                            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                            data-testid="btn-add-material-plan"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Thêm Vật Tư
                        </Button>
                    </div>
                )}
            </div>

            <div className="border rounded-md shadow-sm bg-white overflow-x-auto">
                <Table>
                    <TableHeader className="bg-slate-50/90">
                        <TableRow>
                            <TableHead className="min-w-[270px]">Vật Tư (Chọn kho / Tự sinh mã)</TableHead>
                            <TableHead className="min-w-[160px]">Mã VT & Phân loại</TableHead>
                            <TableHead className="w-[100px]">Định Lượng</TableHead>
                            <TableHead className="w-[130px]">Khổ (mm)</TableHead>
                            <TableHead className="text-right w-[110px]">SL Cần (Tờ)</TableHead>
                            <TableHead className="text-right w-[105px]">Bù Hao (Tờ)</TableHead>
                            <TableHead className="text-right w-[115px] bg-indigo-50/60">Tổng (Tờ)</TableHead>
                            <TableHead className="min-w-[200px]">Quy đổi & Ghi chú</TableHead>
                            <TableHead className="text-right pr-3 w-[95px]">Thao tác</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {Object.entries(groupedPlans).map(([groupKey, plansArray]) => (
                            <React.Fragment key={groupKey}>
                                <TableRow className="bg-slate-100/60 hover:bg-slate-100/60">
                                    <TableCell colSpan={9} className="font-semibold text-slate-700 py-1.5">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="bg-white text-xs">
                                                    {viewMode === 'component' ? 'Thành phần' : 'Bài in'}: {groupKey}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    ({(plansArray as any[]).length} dòng vật tư)
                                                </span>
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                {(plansArray as any[]).map((plan) => {
                                    const rowKey = plan._rowKey;
                                    const isFilm =
                                        plan.materialCategory === 'FILM' ||
                                        String(plan.materialCode || '').toUpperCase().includes('MANG') ||
                                        String(plan.materialName || '').toLowerCase().includes('màng');
                                    const selectValue =
                                        plan.plannedMaterialId || plan.materialId || plan.materialCode || '';

                                    return (
                                        <TableRow
                                            key={rowKey}
                                            data-testid={`material-plan-row-${plan.materialCode || rowKey}`}
                                            className={cn(
                                                plan.isUserCustomized && 'bg-amber-50/20',
                                                isFilm && 'bg-sky-50/15'
                                            )}
                                        >
                                            {/* Cột 1: Combobox chọn Vật tư + Tên vật tư tùy chỉnh */}
                                            <TableCell className="p-2 align-top">
                                                <div className="space-y-1.5">
                                                    <MaterialPickerCombobox
                                                        value={selectValue}
                                                        onChange={() => {}}
                                                        onMaterialSelect={(material) =>
                                                            handleMaterialSelect(material, rowKey)
                                                        }
                                                        hideLabel
                                                        disabled={isLocked}
                                                        fallbackCode={plan.materialCode}
                                                        fallbackName={plan.materialName}
                                                        fallbackWidth={plan.widthMm || plan.rollWidthMm}
                                                        fallbackLength={plan.lengthMm || plan.feedLengthMm}
                                                    />
                                                    <Input
                                                        value={plan.materialName || ''}
                                                        disabled={isLocked}
                                                        onChange={(e) =>
                                                            updateMaterialPlan(rowKey, {
                                                                materialName: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Tên vật tư (VD: Màng BOPP Mờ khổ 645mm)..."
                                                        className="h-7 text-xs bg-slate-50/60"
                                                        data-testid={`input-material-name-${rowKey}`}
                                                    />
                                                </div>
                                            </TableCell>

                                            {/* Cột 2: Mã VT (Editable) + Badges nguồn gốc */}
                                            <TableCell className="p-2 align-top">
                                                <div className="space-y-1">
                                                    <Input
                                                        value={plan.materialCode || ''}
                                                        disabled={isLocked}
                                                        onChange={(e) => {
                                                            const code = e.target.value;
                                                            updateMaterialPlan(rowKey, {
                                                                materialCode: code,
                                                                plannedMaterialId: plan.isAutoGenerated
                                                                    ? code
                                                                    : plan.plannedMaterialId,
                                                            });
                                                        }}
                                                        placeholder="Mã VT (VD: VT-MANG-MO-645)"
                                                        className="h-7 text-xs font-mono font-semibold text-indigo-900"
                                                        data-testid={`input-material-code-${rowKey}`}
                                                    />
                                                    <div className="flex flex-wrap gap-1">
                                                        {plan.isAutoGenerated ? (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-800 border-amber-300"
                                                            >
                                                                Tự sinh mã mới
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200"
                                                            >
                                                                Kho: {Number(plan.availableStock || 0).toLocaleString()}
                                                            </Badge>
                                                        )}
                                                        {plan.isUserCustomized && (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200"
                                                            >
                                                                Đã tùy chỉnh
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>

                                            {/* Cột 3: Định lượng (Editable) */}
                                            <TableCell className="p-2 align-top">
                                                <Input
                                                    value={plan._gsm || ''}
                                                    disabled={isLocked}
                                                    onChange={(e) =>
                                                        updateMaterialPlan(rowKey, { _gsm: e.target.value })
                                                    }
                                                    placeholder="GSM / Sóng"
                                                    className="h-8 text-xs text-center"
                                                    data-testid={`input-material-gsm-${rowKey}`}
                                                />
                                            </TableCell>

                                            {/* Cột 4: Khổ mm (Editable + Nút đảo khổ ngang/dọc cho màng cán) */}
                                            <TableCell className="p-2 align-top">
                                                <div className="space-y-1">
                                                    <Input
                                                        value={plan._size || ''}
                                                        disabled={isLocked}
                                                        onChange={(e) =>
                                                            updateMaterialPlan(rowKey, { _size: e.target.value })
                                                        }
                                                        placeholder="650x430"
                                                        className="h-8 text-xs font-mono text-center"
                                                        data-testid={`input-material-size-${rowKey}`}
                                                    />
                                                    {isFilm && !isLocked && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleToggleFilmOrientation(plan, rowKey)}
                                                            className="h-6 w-full px-1.5 text-[10px] text-sky-700 hover:text-sky-800 hover:bg-sky-50 border border-sky-200"
                                                            title="Đổi ưu tiên Khổ màng Ngang (-5mm) / Dọc (-5mm)"
                                                            data-testid={`btn-toggle-film-orientation-${rowKey}`}
                                                        >
                                                            <ArrowLeftRight className="w-3 h-3 mr-1" />
                                                            {plan.feedDirection === 'VERTICAL'
                                                                ? 'Đổi Khổ ngang'
                                                                : 'Đổi Khổ dọc'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/* Cột 5: SL Cần (Tờ) */}
                                            <TableCell className="p-2 align-top">
                                                <Input
                                                    type="number"
                                                    disabled={isLocked}
                                                    value={plan.sheetsRequired ?? 0}
                                                    onChange={(e) =>
                                                        updateMaterialPlan(rowKey, {
                                                            sheetsRequired: Math.max(0, Number(e.target.value)),
                                                            isCustomQuantity: true,
                                                        })
                                                    }
                                                    className="w-24 text-right ml-auto h-8 text-xs font-medium"
                                                    data-testid={`input-sheets-required-${rowKey}`}
                                                />
                                            </TableCell>

                                            {/* Cột 6: Bù Hao (Tờ) */}
                                            <TableCell className="p-2 align-top">
                                                <Input
                                                    type="number"
                                                    disabled={isLocked}
                                                    value={plan.wastageSheets ?? 0}
                                                    onChange={(e) =>
                                                        updateMaterialPlan(rowKey, {
                                                            wastageSheets: Math.max(0, Number(e.target.value)),
                                                        })
                                                    }
                                                    className="w-24 text-right ml-auto h-8 text-xs text-amber-700"
                                                    data-testid={`input-wastage-sheets-${rowKey}`}
                                                />
                                            </TableCell>

                                            {/* Cột 7: Tổng (Tờ) - Cho phép sửa trực tiếp hoặc tự cộng */}
                                            <TableCell className="font-semibold text-right bg-indigo-50/20 p-2 align-top">
                                                <Input
                                                    type="number"
                                                    disabled={isLocked}
                                                    value={
                                                        plan.totalSheets ??
                                                        Number(plan.sheetsRequired || 0) +
                                                            Number(plan.wastageSheets || 0)
                                                    }
                                                    onChange={(e) => {
                                                        const newTotal = Math.max(0, Number(e.target.value));
                                                        const waste = Math.max(0, Number(plan.wastageSheets || 0));
                                                        updateMaterialPlan(rowKey, {
                                                            totalSheets: newTotal,
                                                            sheetsRequired: Math.max(0, newTotal - waste),
                                                            _keepDirectTotal: true,
                                                        });
                                                    }}
                                                    className="w-24 text-right ml-auto h-8 text-xs font-bold text-indigo-700 bg-white border-indigo-200"
                                                    data-testid={`input-total-sheets-${rowKey}`}
                                                />
                                            </TableCell>

                                            {/* Cột 8: Quy đổi Màng (m / kg) & Ghi chú */}
                                            <TableCell className="p-2 align-top">
                                                <div className="space-y-1">
                                                    {isFilm && (plan.linearMeters || plan.rollWidthMm) && (
                                                        <div
                                                            className="text-[11px] font-medium px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 flex items-center justify-between gap-1"
                                                            data-testid={`film-conversion-badge-${rowKey}`}
                                                        >
                                                            <span>
                                                                Cuộn {plan.rollWidthMm}mm:{' '}
                                                                <strong>
                                                                    {Number(plan.linearMeters || 0).toLocaleString()}m
                                                                </strong>{' '}
                                                                (~{plan.weightKg || 0}kg)
                                                            </span>
                                                        </div>
                                                    )}
                                                    <Input
                                                        value={plan.notes ?? plan.splitReason ?? ''}
                                                        disabled={isLocked}
                                                        onChange={(e) =>
                                                            updateMaterialPlan(rowKey, {
                                                                notes: e.target.value,
                                                                splitReason: e.target.value,
                                                            })
                                                        }
                                                        className="h-7 text-xs"
                                                        placeholder="Ghi chú vật tư..."
                                                        data-testid={`input-material-notes-${rowKey}`}
                                                    />
                                                </div>
                                            </TableCell>

                                            {/* Cột 9: Thao tác */}
                                            <TableCell className="p-2 align-top">
                                                {!isLocked && (
                                                    <div className="flex justify-end gap-1">
                                                        {plan.isUserCustomized && onRegenerateAutoPlans && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => {
                                                                    updateMaterialPlan(
                                                                        rowKey,
                                                                        { isUserCustomized: false },
                                                                        false
                                                                    );
                                                                    setTimeout(() => onRegenerateAutoPlans(false), 10);
                                                                }}
                                                                title="Khôi phục tính tự động cho dòng này"
                                                                className="h-7 w-7 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                                                            >
                                                                <RotateCcw className="w-3.5 h-3.5" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleSplit(plan, rowKey)}
                                                            title="Tách dòng vật tư"
                                                            className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                        >
                                                            <SplitSquareHorizontal className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeMaterialPlan(rowKey)}
                                                            className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                        {materialPlans.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} className="text-center text-muted-foreground py-10">
                                    <div className="flex flex-col items-center gap-2">
                                        <Package className="w-8 h-8 text-slate-300" />
                                        <p className="text-xs">
                                            Chưa có vật tư nào. Nhập khổ giấy in hoặc chọn sản phẩm để hệ thống tự động sinh Kế hoạch Vật tư (Giấy in, Màng cán, Sóng/Giấy bồi).
                                        </p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
